import { FileUp, Settings, Rocket, Search, FileCheck, Cpu, Users, MessageCircle, CheckCircle2, ArrowRight, Sparkles } from 'lucide-react'
import { useScrollAnim } from '../../hooks/use-scroll-anim'
import { useI18n } from '../../i18n'
import { useNavigate } from 'react-router-dom'

export function HowItWorksSection() {
  const { ref, isVisible } = useScrollAnim()
  const { t } = useI18n()
  const navigate = useNavigate()

  const steps = [
    {
      number: '01',
      title: t('知识导入', 'Knowledge Import'),
      description: t('上传企业文档，AI自动解析构建知识索引', 'Upload docs, AI auto-parses and builds knowledge index'),
      icon: FileUp,
      color: 'text-indigo-500',
      iconBg: 'bg-indigo-50',
      ring: 'ring-indigo-200',
      details: [
        { icon: FileUp, text: t('支持 PDF / Word / Excel', 'PDF / Word / Excel supported') },
        { icon: Search, text: t('AI 自动解析文档语义', 'AI auto-parses document semantics') },
        { icon: FileCheck, text: t('分段索引，精准检索', 'Segment indexing for precise retrieval') },
      ],
    },
    {
      number: '02',
      title: t('管家配置', 'Butler Config'),
      description: t('设定角色与范围，绑定企业专属AI应用', 'Set role & scope, bind dedicated AI apps'),
      icon: Settings,
      color: 'text-violet-500',
      iconBg: 'bg-violet-50',
      ring: 'ring-violet-200',
      details: [
        { icon: Settings, text: t('角色定位与服务范围', 'Role positioning & service scope') },
        { icon: Cpu, text: t('DeepSeek / Qwen 模型选择', 'DeepSeek / Qwen model selection') },
        { icon: Users, text: t('Token 额度与权限分配', 'Token quota & permission allocation') },
      ],
    },
    {
      number: '03',
      title: t('即刻上岗', 'Go Live'),
      description: t('一键上线，全员即可对话获取精准答案', 'One-click go live, everyone gets instant answers'),
      icon: Rocket,
      color: 'text-orange-500',
      iconBg: 'bg-orange-50',
      ring: 'ring-orange-200',
      details: [
        { icon: Rocket, text: t('一键上线，即刻使用', 'One-click go live, ready to use') },
        { icon: MessageCircle, text: t('SSE 流式实时响应', 'SSE streaming real-time response') },
        { icon: CheckCircle2, text: t('对话反馈持续优化', 'Continuous improvement from feedback') },
      ],
    },
  ]

  return (
    <section id="how-it-works" className="py-24 min-h-screen flex items-center" style={{ scrollSnapAlign: 'start' }}>
      <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block px-3 py-1 text-xs font-medium text-violet-500 bg-violet-50/80 rounded-full mb-4">
            {t('快速上手', 'Quick Start')}
          </span>
          <h2 className="text-2xl md:text-4xl font-semibold text-[#1a1a1a] mb-3 tracking-tight">
            {t('三步让AI管家上岗', 'Three Steps to Your AI Butler')}
          </h2>
          <p className="text-sm text-gray-400 max-w-lg mx-auto">
            {t('从知识导入到正式服务，最快30分钟即可完成', 'From knowledge import to live service in as fast as 30 minutes')}
          </p>
        </div>

        {/* Vertical timeline */}
        <div className="relative max-w-md mx-auto">
          {/* Center line — extends through to CTA */}
          <div className="absolute left-[27px] top-0 bottom-0 w-[2px] rounded-full overflow-hidden">
            <div className="w-full h-full bg-gradient-to-b from-indigo-200 via-violet-200 via-orange-200 to-orange-200/40" />
          </div>

          <div className="space-y-10">
            {steps.map((step, i) => (
              <div
                key={step.number}
                className={`relative flex gap-6 transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: isVisible ? `${i * 200}ms` : '0ms' }}
              >
                {/* Circle node */}
                <div className="relative z-10 shrink-0">
                  <div className={`h-14 w-14 rounded-full bg-white ring-2 ${step.ring} shadow-sm flex items-center justify-center`}>
                    <step.icon className={`h-6 w-6 ${step.color}`} />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 pt-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className={`text-[11px] font-bold ${step.color} tracking-widest`}>{t('步骤', 'STEP')} {step.number}</span>
                    <h3 className="text-lg font-bold text-[#1a1a1a]">{step.title}</h3>
                  </div>
                  <p className="text-xs text-gray-400 mb-4">{step.description}</p>

                  {/* Detail pills */}
                  <div className="flex flex-wrap gap-2">
                    {step.details.map((d, j) => (
                      <span key={j} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ${step.iconBg} text-[11px] font-medium ${step.color}`}>
                        <d.icon className="h-3 w-3" />
                        {d.text}
                      </span>
                    ))}
                  </div>

                  {/* Arrow to next step */}
                  {i < steps.length - 1 && (
                    <div className="mt-4 flex justify-center">
                      <ArrowRight className={`h-4 w-4 ${step.color} opacity-30 rotate-90`} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* One-click shortcut CTA — connected to timeline */}
        <div className="relative max-w-md mx-auto mt-8">
          {/* Connecting dot aligned to timeline center */}
          <div className="absolute left-[27px] top-0 -translate-y-1/2 z-10">
            <span className="relative flex h-8 w-8">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-300 opacity-40" />
              <span className="relative inline-flex rounded-full h-8 w-8 bg-gradient-to-br from-orange-400 to-amber-400 shadow-sm items-center justify-center">
                <Sparkles className="h-4 w-4 text-white" />
              </span>
            </span>
          </div>

          <div
            className={`ml-[60px] transition-all duration-700 delay-[800ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            <button
              onClick={() => navigate('/generate')}
              className="group inline-flex flex-col items-start gap-1 rounded-2xl border border-dashed border-orange-200 bg-gradient-to-br from-orange-50/60 to-amber-50/40 px-6 py-4 hover:border-orange-400 hover:from-orange-50 hover:to-amber-50 hover:shadow-md transition-all duration-300 text-left"
            >
              <span className="text-xs text-gray-400">
                {t('操作太复杂？', 'Too complicated?')}
                <span className="mx-1">·</span>
                {t('不懂文件管理？', 'Not sure about file management?')}
              </span>
              <span className="flex items-center gap-1.5 text-sm font-semibold text-orange-500 group-hover:text-orange-600">
                {t('没关系，我们为你一键生成', 'No worries — we\'ll generate it for you')}
                <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}
